import { useState, useEffect, useCallback, useRef, useMemo, memo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Calendar,
  Clock,
  History,
  Plus,
  Users,
  CheckSquare,
  Settings,
  BarChart3,
  FileText,
  Award,
  StickyNote,
  FileSpreadsheet,
  ChevronLeft,
  Briefcase
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// Debounce delay for realtime events (ms)
const DEBOUNCE_DELAY = 500;

// LocalStorage key for sidebar state
const SIDEBAR_COLLAPSED_KEY = 'attendance-sidebar-collapsed';

// Animation config - defined outside component to prevent recreation
const SIDEBAR_TRANSITION = { duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] };

// Static navigation structure - icons and routes don't change
const NAV_STRUCTURE = [
  {
    name: 'Overview',
    items: [
      { name: 'Dashboard', href: '/attendance', icon: BarChart3, exactMatch: true }
    ]
  },
  {
    name: 'My Leave',
    items: [
      { name: 'Request Leave', href: '/attendance/request', icon: Plus },
      { name: 'My Leave', href: '/attendance/history', icon: History },
      { name: 'Team Calendar', href: '/attendance/calendar', icon: Calendar }
    ]
  },
  {
    name: 'Management',
    adminOnly: true,
    items: [
      { name: 'Team Overview', href: '/attendance/team', icon: Users },
      { name: 'All Requests', href: '/attendance/requests', icon: CheckSquare, hasBadge: true },
      { name: 'Reports', href: '/attendance/reports', icon: FileText }
    ]
  },
  {
    name: 'Settings',
    adminOnly: true,
    items: [
      { name: 'Employment Types', href: '/attendance/employment-types', icon: Briefcase },
      { name: 'Entitlements', href: '/attendance/entitlements', icon: Award },
      { name: 'Bank Holidays', href: '/attendance/bank-holidays', icon: Calendar },
      { name: 'Calendar Notes', href: '/attendance/calendar-notes', icon: StickyNote },
      { name: 'Import/Export', href: '/attendance/import-export', icon: FileSpreadsheet },
      { name: 'Settings', href: '/attendance/settings', icon: Settings }
    ]
  }
];

/**
 * Memoized nav item component to prevent re-renders
 */
const NavItem = memo(({ item, isActive, isCollapsed, badge }) => {
  const Icon = item.icon;

  const linkContent = (
    <Link
      to={item.href}
      className={cn(
        "flex items-center text-sm font-medium rounded-xl transition-colors duration-150",
        isCollapsed
          ? "justify-center w-9 h-9 mx-auto"
          : "gap-3 px-3 py-2.5",
        isActive
          ? "bg-slate-800 text-white shadow-sm"
          : "text-slate-600 hover:bg-slate-50 hover:text-slate-800"
      )}
    >
      <div className="flex items-center justify-center flex-shrink-0 relative">
        <Icon className={cn(
          "flex-shrink-0",
          isCollapsed ? "h-5 w-5" : "h-4 w-4"
        )} />
        {/* Badge indicator for collapsed state */}
        {isCollapsed && badge && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {badge > 9 ? '9+' : badge}
          </span>
        )}
      </div>
      {!isCollapsed && (
        <span className="flex-1 whitespace-nowrap">
          {item.name}
        </span>
      )}
      {!isCollapsed && badge && (
        <Badge className="bg-amber-500 hover:bg-amber-500 text-white px-2 py-0.5 text-xs font-semibold rounded-full">
          {badge}
        </Badge>
      )}
    </Link>
  );

  // Wrap in tooltip when collapsed
  if (isCollapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {linkContent}
        </TooltipTrigger>
        <TooltipContent side="right" className="rounded-lg">
          <p className="font-medium">{item.name}</p>
          {badge && (
            <p className="text-xs text-amber-500">{badge} pending</p>
          )}
        </TooltipContent>
      </Tooltip>
    );
  }

  return linkContent;
});

NavItem.displayName = 'NavItem';

/**
 * Memoized nav group component
 */
const NavGroup = memo(({ group, pathname, isCollapsed, pendingCount }) => {
  return (
    <div>
      {!isCollapsed && (
        <h3 className="px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
          {group.name}
        </h3>
      )}
      <div className="space-y-1">
        {group.items.map((item) => {
          const isActive = item.exactMatch
            ? pathname === item.href
            : pathname === item.href;
          const badge = item.hasBadge && pendingCount > 0 ? pendingCount : null;

          return (
            <NavItem
              key={item.href}
              item={item}
              isActive={isActive}
              isCollapsed={isCollapsed}
              badge={badge}
            />
          );
        })}
      </div>
    </div>
  );
});

NavGroup.displayName = 'NavGroup';

/**
 * Memoized header component
 */
const SidebarHeader = memo(({ isCollapsed }) => (
  <div className={cn(
    "border-b border-slate-100",
    isCollapsed ? "p-3" : "p-4"
  )}>
    <div className={cn(
      "flex items-center",
      isCollapsed ? "justify-center" : "gap-3"
    )}>
      <div className={cn(
        "rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0",
        isCollapsed ? "w-9 h-9" : "w-10 h-10"
      )}>
        <Clock className={cn(
          "text-slate-700",
          isCollapsed ? "h-4 w-4" : "h-5 w-5"
        )} />
      </div>
      {!isCollapsed && (
        <div className="overflow-hidden">
          <h2 className="text-sm font-semibold text-slate-800 whitespace-nowrap">Attendance</h2>
          <p className="text-xs text-slate-500 whitespace-nowrap">Global View</p>
        </div>
      )}
    </div>
  </div>
));

SidebarHeader.displayName = 'SidebarHeader';

/**
 * Memoized collapse toggle component
 */
const CollapseToggle = memo(({ isCollapsed, onToggle }) => (
  <div className={cn(
    "border-t border-slate-100",
    isCollapsed ? "p-2 flex justify-center" : "p-3"
  )}>
    <button
      onClick={onToggle}
      className={cn(
        "flex items-center gap-2 text-sm font-medium text-slate-500 rounded-xl transition-colors duration-150 hover:bg-slate-50 hover:text-slate-700",
        isCollapsed ? "justify-center w-9 h-9 p-0" : "w-full px-3 py-2"
      )}
    >
      <motion.div
        animate={{ rotate: isCollapsed ? 180 : 0 }}
        transition={SIDEBAR_TRANSITION}
      >
        <ChevronLeft className="h-4 w-4" />
      </motion.div>
      {!isCollapsed && (
        <span className="whitespace-nowrap">Collapse</span>
      )}
    </button>
  </div>
));

CollapseToggle.displayName = 'CollapseToggle';

/**
 * Main sidebar component
 */
export const AttendanceSidebar = memo(({ onCollapsedChange }) => {
  const location = useLocation();
  const pathname = location.pathname;
  const { can, userProfile } = useAuth();
  const [pendingCount, setPendingCount] = useState(0);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return stored === 'true';
  });

  // Refs for debouncing and cleanup
  const debounceRef = useRef(null);
  const mountedRef = useRef(true);

  // Check if user can manage attendance (admin-level access)
  const canManageAttendance = can('attendance.manage');

  // Notify parent of collapse state changes
  useEffect(() => {
    onCollapsedChange?.(isCollapsed);
  }, [isCollapsed, onCollapsedChange]);

  // Toggle collapse state - stable callback
  const toggleCollapsed = useCallback(() => {
    setIsCollapsed(prev => {
      const newValue = !prev;
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(newValue));
      return newValue;
    });
  }, []);

  /**
   * Fetch pending count using optimized RPC
   */
  const fetchPendingCount = useCallback(async () => {
    if (!userProfile?.company_id) return;

    try {
      const { data, error } = await supabase.rpc(
        'get_pending_leave_count',
        { p_company_id: userProfile.company_id }
      );

      if (error) throw error;

      if (mountedRef.current) {
        setPendingCount(data || 0);
      }
    } catch (error) {
      console.error('[AttendanceSidebar] Error fetching pending count:', error);
    }
  }, [userProfile?.company_id]);

  /**
   * Debounced fetch to prevent rapid refetches from realtime events
   */
  const debouncedFetch = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchPendingCount();
    }, DEBOUNCE_DELAY);
  }, [fetchPendingCount]);

  // Initial fetch and realtime subscription
  useEffect(() => {
    if (!canManageAttendance || !userProfile?.company_id) return;

    mountedRef.current = true;
    fetchPendingCount();

    // Set up real-time subscription for leave_requests changes
    const channel = supabase
      .channel('leave_requests_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'leave_requests'
        },
        () => {
          debouncedFetch();
        }
      )
      .subscribe();

    return () => {
      mountedRef.current = false;
      channel.unsubscribe();
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [canManageAttendance, userProfile?.company_id, fetchPendingCount, debouncedFetch]);

  // Memoize filtered navigation based on permissions
  const visibleNavigation = useMemo(() => {
    return NAV_STRUCTURE.filter(group => {
      if (!group.adminOnly) return true;
      return canManageAttendance;
    });
  }, [canManageAttendance]);

  return (
    <TooltipProvider delayDuration={0}>
      <motion.nav
        initial={false}
        animate={{ width: isCollapsed ? 64 : 256 }}
        transition={SIDEBAR_TRANSITION}
        className="flex flex-col h-full bg-white border-r border-slate-200 relative overflow-hidden"
      >
        <SidebarHeader isCollapsed={isCollapsed} />

        {/* Navigation Groups */}
        <div className={cn(
          "flex-1 overflow-y-auto py-4",
          isCollapsed ? "px-2 space-y-2" : "px-3 space-y-6"
        )}>
          {visibleNavigation.map((group) => (
            <NavGroup
              key={group.name}
              group={group}
              pathname={pathname}
              isCollapsed={isCollapsed}
              pendingCount={pendingCount}
            />
          ))}
        </div>

        <CollapseToggle isCollapsed={isCollapsed} onToggle={toggleCollapsed} />
      </motion.nav>
    </TooltipProvider>
  );
});

AttendanceSidebar.displayName = 'AttendanceSidebar';
